import {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useLocation } from 'react-router';
import usePrevious from '@stripcash/ui-kit/utils/hooks/usePrevious';
import cn from 'classnames';
import { ThemeVariants, useTheme } from '@stripcash/ui-kit/hoc/Theme';
import sysend from 'sysend';

import SystemNotificationsContainer from 'common/components/SystemNotificationsContainer';
import ModalsContainer from 'common/components/ModalsContainer';
import { ErrorBoundary } from 'common/components/ErrorBoundary';
import ScrollTopButton from 'common/components/ScrollTopButton';
import createGlobalApp from 'common/apps/apps/global';
import App from 'common/apps/core/App';
import Notifications from 'common/apps/modules/notifications/Notifications';
import useApp from 'common/helpers/useApp';
import {
  MAIN_CONTENT_CONTAINER_ID,
  SCROLL_CONTAINER_ID,
} from 'common/constants/ui';
import Modals from 'common/apps/modules/modals/Modals';
import {
  GlobalApiContext,
  GlobalStateContext,
} from 'common/apps/core/contexts';
import SurveyQuestions from 'common/components/SurveyQuestions';
import { GlobalAppType } from 'common/apps/apps/global/types';
import { CHECK_THEME_SYSEND_EVENT } from 'common/constants/common';

import styles from './styles.module.css';

const globalContexts = { state: GlobalStateContext, api: GlobalApiContext };

type MainLayoutProps = PropsWithChildren<{
  sidebar: ReactNode;
  header: ReactNode;
}>;

const MainLayout: FC<MainLayoutProps> = ({ children, sidebar, header }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const { onThemeToggle, currentTheme } = useTheme();

  const prevPathname = usePrevious(pathname);

  useEffect(() => {
    if (prevPathname !== pathname && sectionRef.current) {
      sectionRef.current.scrollTop = 0;
    }
  }, [prevPathname, pathname]);

  const handleThemeChange = useCallback(
    ({ themeId }: { themeId: ThemeVariants }) => {
      if (currentTheme !== themeId) {
        onThemeToggle();
      }
    },
    [currentTheme, onThemeToggle],
  );

  useEffect(() => {
    sysend.on(CHECK_THEME_SYSEND_EVENT, handleThemeChange);

    return () => {
      sysend.off(CHECK_THEME_SYSEND_EVENT, handleThemeChange);
    };
  }, [handleThemeChange]);

  const globalApp = useApp(createGlobalApp, GlobalAppType.MainApp)!;

  return (
    <div className={styles.mainLayout}>
      {header}
      <App app={globalApp} contexts={globalContexts}>
        <Modals containerId="global" />
        <Notifications />
        <SystemNotificationsContainer />
        <div className={styles.mainLayoutBody}>
          {sidebar}
          <ErrorBoundary>
            <div
              className={styles.contentWrapper}
              id={MAIN_CONTENT_CONTAINER_ID}
            >
              {APP_NAME === 'affiliates' && (
                <SurveyQuestions
                  onShowModal={globalApp.api.survey.handleShowModal}
                />
              )}
              <section
                id={SCROLL_CONTAINER_ID}
                className={styles.pageWrapper}
                ref={sectionRef}
              >
                {children}
                <ScrollTopButton scrollElementRef={sectionRef.current} />
              </section>
            </div>
          </ErrorBoundary>
        </div>
      </App>
      <ModalsContainer />
      <div
        id="notifications"
        className={cn(styles.ukNotify, styles.ukNotifyTopCenter)}
      />
    </div>
  );
};

export default MainLayout;
